/** Browser client for the self-hosted API (replaces Supabase). */

const TOKEN_KEY = "motive_sm_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Production API origin baked in as a fallback when VITE_API_URL is unset
// (e.g. Cloudflare Pages env vars not applied). In `vite dev` we keep the
// empty string so requests stay relative and hit Vite's /api proxy.
const PROD_API_FALLBACK = "https://api.datalycloud.in";

function apiBase(): string {
  const fromEnv = import.meta.env.VITE_API_URL;
  if (fromEnv != null && fromEnv !== "") return fromEnv.replace(/\/$/, "");
  if (import.meta.env.PROD) return PROD_API_FALLBACK;
  return "";
}

/** Thrown when `fetch` fails (offline, wrong URL, CORS, TLS). Includes actionable hints. */
export class ApiNetworkError extends Error {
  constructor(attemptedUrl: string, cause?: unknown) {
    const base = apiBase();
    const envUrl = import.meta.env.VITE_API_URL?.trim();
    const prodConnectivity =
      " The browser could not complete the request — check network, DNS/TLS, that the API is up, and that CORS on the API allows this site’s origin (server CORS_ORIGIN / TRUST_PROXY behind tunnels).";
    const prodEnvNote =
      envUrl && envUrl !== ""
        ? ""
        : ` This build had no VITE_API_URL; using "${PROD_API_FALLBACK}". Commit repo-root .env.production or set VITE_API_URL in CI/Pages and rebuild (see .env.production.example).`;
    const hint = import.meta.env.DEV
      ? " Start the API (server/: npm run dev, port 8787) and run npm run dev from the repo root so /api proxies to it."
      : import.meta.env.PROD
        ? `${prodConnectivity}${prodEnvNote}`
        : "";
    super(
      `Could not reach the server (requested ${attemptedUrl}${base ? `; API base ${base}` : ""}).${hint}`,
    );
    this.name = "ApiNetworkError";
    Object.setPrototypeOf(this, ApiNetworkError.prototype);
    if (cause !== undefined) (this as Error & { cause?: unknown }).cause = cause;
  }
}

export function apiUrl(path: string): string {
  if (path.startsWith("http")) return path;
  const base = apiBase();
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

export type SessionUser = { id: string; email: string };

/** Legacy alias — Session always carries `user`, plus MFA status when fetched via `/auth/session`. */
export type Session = {
  user: SessionUser;
  mfa?: {
    enabled: boolean;
    verified: boolean;
    required_for_all: boolean;
  };
};

async function parseError(res: Response): Promise<string> {
  try {
    const j = (await res.json()) as { error?: string; message?: string };
    return j.error ?? j.message ?? res.statusText;
  } catch {
    return res.statusText;
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const url = apiUrl(path);
  let res: Response;
  try {
    res = await fetch(url, { ...init, headers });
  } catch (e: unknown) {
    console.warn("[apiFetch] network failure", url, e);
    throw new ApiNetworkError(url, e);
  }
  if (!res.ok) throw new Error(await parseError(res));
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * Result of a login attempt. The API now has three terminal states:
 *  - signed in (full session token)
 *  - MFA required (TOTP challenge with `challenge_token`)
 *  - MFA enrollment required (`enrollment_token` for /auth/mfa/enroll/init)
 */
export type LoginResult =
  | { kind: "signed_in"; user: SessionUser }
  | { kind: "mfa_required"; challenge_token: string; user: SessionUser }
  | { kind: "mfa_enroll_required"; enrollment_token: string; user: SessionUser };

export async function login(email: string, password: string): Promise<LoginResult> {
  const data = await apiFetch<{
    token?: string;
    user: SessionUser;
    mfa_required?: boolean;
    challenge_token?: string;
    mfa_enroll_required?: boolean;
    enrollment_token?: string;
  }>("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  if (data.token) {
    setToken(data.token);
    return { kind: "signed_in", user: data.user };
  }
  if (data.mfa_required && data.challenge_token) {
    return { kind: "mfa_required", challenge_token: data.challenge_token, user: data.user };
  }
  if (data.mfa_enroll_required && data.enrollment_token) {
    return {
      kind: "mfa_enroll_required",
      enrollment_token: data.enrollment_token,
      user: data.user,
    };
  }
  throw new Error("invalid_login_response");
}

export type RegisterPayload = {
  email: string;
  password: string;
  fullName: string;
  phone?: string;
  companyName: string;
};

export type OrgRole = "dataly_admin" | "client_admin" | "client_staff" | "public_user";

export type RegisterResult = {
  user: SessionUser;
  organization: { id: string; record_id?: string; name: string; slug: string };
  role: OrgRole;
  /** When MFA policy is `all_required`, this is set and a full session is **not** issued. */
  mfa_enroll_required?: boolean;
  enrollment_token?: string;
};

export async function register(payload: RegisterPayload): Promise<RegisterResult> {
  const data = await apiFetch<{
    token?: string;
    user: SessionUser;
    organization: { id: string; record_id?: string; name: string; slug: string };
    role: OrgRole;
    mfa_enroll_required?: boolean;
    enrollment_token?: string;
  }>("/api/v1/auth/register", {
    method: "POST",
    body: JSON.stringify({
      email: payload.email,
      password: payload.password,
      full_name: payload.fullName,
      phone: payload.phone ?? "",
      company_name: payload.companyName,
    }),
  });
  if (data.token) setToken(data.token);
  return {
    user: data.user,
    organization: data.organization,
    role: data.role,
    mfa_enroll_required: data.mfa_enroll_required,
    enrollment_token: data.enrollment_token,
  };
}

/* ─────────────── MFA & sessions ─────────────── */

export type MfaEnrollInit = {
  otpauth_url: string;
  qr_data_url: string;
  secret: string;
};

export async function mfaEnrollInit(enrollmentToken: string): Promise<MfaEnrollInit> {
  return apiFetch<MfaEnrollInit>("/api/v1/auth/mfa/enroll/init", {
    method: "POST",
    body: JSON.stringify({ enrollment_token: enrollmentToken }),
  });
}

export async function mfaEnrollConfirm(
  enrollmentToken: string,
  code: string,
): Promise<{ recovery_codes: string[]; user: SessionUser }> {
  const data = await apiFetch<{
    token: string;
    recovery_codes: string[];
    user: SessionUser;
  }>("/api/v1/auth/mfa/enroll/confirm", {
    method: "POST",
    body: JSON.stringify({ enrollment_token: enrollmentToken, code }),
  });
  setToken(data.token);
  return { recovery_codes: data.recovery_codes, user: data.user };
}

export async function mfaChallengeVerify(
  challengeToken: string,
  code: string,
): Promise<{ user: SessionUser }> {
  const data = await apiFetch<{ token: string; user: SessionUser }>(
    "/api/v1/auth/mfa/challenge/verify",
    {
      method: "POST",
      body: JSON.stringify({ challenge_token: challengeToken, code }),
    },
  );
  setToken(data.token);
  return { user: data.user };
}

export async function mfaRecoveryUse(
  challengeToken: string,
  code: string,
): Promise<{ user: SessionUser }> {
  const data = await apiFetch<{ token: string; user: SessionUser }>(
    "/api/v1/auth/mfa/recovery/use",
    {
      method: "POST",
      body: JSON.stringify({ challenge_token: challengeToken, code }),
    },
  );
  setToken(data.token);
  return { user: data.user };
}

export async function mfaDisable(args: { password?: string; code?: string }): Promise<void> {
  await apiFetch("/api/v1/me/mfa/disable", {
    method: "POST",
    body: JSON.stringify(args),
  });
}

export async function mfaRegenerateRecovery(code: string): Promise<{ recovery_codes: string[] }> {
  return apiFetch("/api/v1/me/mfa/recovery/regen", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export type SessionListEntry = {
  id: string;
  token_jti: string;
  user_agent: string | null;
  ip: string | null;
  label: string | null;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  revoked_at: string | null;
  mfa_verified: boolean;
  is_current: boolean;
};

export async function listSessions(): Promise<{
  current_jti: string | null;
  sessions: SessionListEntry[];
}> {
  return apiFetch("/api/v1/me/sessions");
}

export async function revokeSessionApi(jti: string): Promise<void> {
  await apiFetch(`/api/v1/me/sessions/${encodeURIComponent(jti)}`, { method: "DELETE" });
}

export async function revokeOtherSessions(): Promise<{ revoked: number }> {
  return apiFetch(`/api/v1/me/sessions/revoke-others`, { method: "POST", body: "{}" });
}

export async function logoutApi(): Promise<void> {
  try {
    await apiFetch("/api/v1/auth/logout", { method: "POST", body: "{}" });
  } catch {
    /* token may already be invalid; clear locally regardless */
  }
  clearToken();
}

/**
 * Translate the API's error code (or a fetch failure) into a sentence the user
 * can act on. Falls back to a generic "try again / reload / clear cache" hint.
 */
export function friendlyAuthError(err: unknown): string {
  if (err instanceof ApiNetworkError) {
    return err.message;
  }
  const code = err instanceof Error ? err.message : String(err);
  switch (code) {
    case "email_taken":
      return "This email is already registered. Try a different email or sign in instead.";
    case "duplicate_organization":
      return "An organization with this name already exists. Choose a different company or workspace name.";
    case "invalid_credentials":
      return "Email and password don't match. Try again.";
    case "user_not_registered":
      return "No account exists with that email. Ask them to sign up first, then add them again.";
    case "cannot_change_role":
      return "That member cannot be converted to staff (already an administrator).";
    case "invalid_email":
      return "Use an email address with text before and after the @ sign (any domain is fine).";
    case "weak_password":
      return "Use a password with at least 6 characters.";
    case "invalid_payload":
      return "Please fill in every required field (full name, company, email, password).";
    case "platform_admin_required":
      return "That action needs platform Dataly admin privileges. Sign out and back in, or ask an operator to grant dataly_admin in organization_members.";
    case "user_delete_blocked_references":
      return "This account can't be deleted while older rows still reference it in the database. Run pending migrations on the API server (npm run migrate), then try again.";
    case "email_not_configured":
      return "Outbound email isn’t configured yet. Open Admin profile → SMTP relay and save workspace or platform SMTP settings (or set SMTP_* env vars on the API). For local dev only you can use STAFF_PROVISION_RETURN_PASSWORD=1.";
    case "invalid_sender_email":
      return "Use a valid email address for the outgoing From field, or leave it blank.";
    case "invalid_vault_access":
      return "Store access choices are invalid. Reload and try again.";
    case "rate_limited":
      return "Too many attempts. Wait a minute and try again.";
    case "account_disabled":
      return "This account has been deactivated. Contact your organization administrator.";
    case "invalid_code":
      return "That code didn't match. Try again with the latest 6-digit code from your authenticator.";
    case "mfa_required":
      return "Multi-factor authentication is required. Sign out and sign in again to verify.";
    case "mfa_not_enabled":
      return "Two-factor authentication isn't enabled on this account.";
    case "step_up_required":
      return "This token isn't a full session. Sign in again to continue.";
    case "session_revoked":
      return "This session was revoked. Sign in again.";
    case "session_expired":
      return "This session expired. Sign in again.";
    case "verification_failed":
      return "We couldn't verify your password or authenticator code. Try again.";
    case "no_pending_enrollment":
      return "Your enrollment session timed out. Start sign-in again.";
    case "invalid_step_up_token":
      return "Your enrollment / challenge token expired. Start sign-in again.";
    case "approval_required":
      return "This vault requires approval. Your change is pending review by a Client admin.";
    case "cannot_self_approve":
      return "You can't approve a request you filed yourself. Ask another admin.";
    case "encryption_unconfigured":
      return "The API server doesn't have SECRETS_MASTER_KEY set. Ask an operator.";
    case "invalid_tags":
      return "Tags must be lowercase letters/numbers/dots/dashes/underscores, max 64 chars each.";
    case "invalid_url":
      return "Webhook URLs must use https://.";
    case "forbidden":
      return "You don't have permission for this action on this workspace.";
    case "not_found":
    case "Not Found":
      return "This feature isn't available on the API server yet. Re-deploy the API and run database migrations (server: npm run migrate), then reload.";
    case "server_error":
    case "Internal Server Error":
      return "The API server hit an unexpected error. Check the API logs (deploy/m2/README.md), confirm migrations have been applied (server: npm run migrate), and try again.";
    case "Failed to fetch":
    case "NetworkError when attempting to fetch resource.":
    case "Load failed":
      return "Couldn't reach the server. Reload the page, clear your browser cache or session, and try again.";
    default:
      return "Something went wrong. Please try again, or reload the page and clear your browser cache/session.";
  }
}

export async function fetchSession(): Promise<Session> {
  return apiFetch<Session>("/api/v1/auth/session");
}

export async function logout() {
  await logoutApi();
}

type MembershipRow = {
  organization_id: string;
  role: string;
  organizations: {
    id: string;
    record_id?: string;
    name: string;
    slug: string;
    active?: boolean;
  } | null;
};

export async function loadMemberships(): Promise<MembershipRow[]> {
  const data = await apiFetch<{ memberships: MembershipRow[] }>("/api/v1/memberships");
  return data.memberships;
}

export async function bootstrapOrganization(orgName: string, orgSlug: string): Promise<string> {
  const data = await apiFetch<{ organization_id: string }>("/api/v1/organizations/bootstrap", {
    method: "POST",
    body: JSON.stringify({ org_name: orgName, org_slug: orgSlug }),
  });
  return data.organization_id;
}

export async function loadVaults(orgId: string) {
  const data = await apiFetch<{ vaults: unknown[] }>(`/api/v1/organizations/${orgId}/vaults`);
  return data.vaults;
}

export async function loadMembers(orgId: string) {
  const data = await apiFetch<{ members: unknown[] }>(`/api/v1/organizations/${orgId}/members`);
  return data.members;
}

/** Client admin only — add registered user as `client_staff` (or upgrade `public_user`). */
export async function inviteOrgStaff(orgId: string, email: string): Promise<void> {
  await apiFetch(`/api/v1/organizations/${orgId}/members/staff`, {
    method: "POST",
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  });
}

export type MyProfileResponse = {
  user: { id: string; email: string; record_id?: string };
  profile: { display_name: string | null; phone: string | null; sender_email: string | null };
};

export async function fetchMyProfile(): Promise<MyProfileResponse> {
  return apiFetch<MyProfileResponse>("/api/v1/me");
}

export async function patchMyProfile(patch: {
  display_name?: string | null;
  phone?: string | null;
  sender_email?: string | null;
}): Promise<MyProfileResponse> {
  return apiFetch<MyProfileResponse>("/api/v1/me", {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export type SmtpSettingsPublic = {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  has_password: boolean;
  default_from: string;
};

export type SmtpSettingsPatch = Partial<{
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
  default_from: string;
}>;

/** Client admin or Dataly admin — SMTP used first for this workspace’s invitations. */
export async function fetchOrgSmtpSettings(orgId: string): Promise<{ settings: SmtpSettingsPublic }> {
  return apiFetch(`/api/v1/organizations/${orgId}/smtp-settings`);
}

export async function patchOrgSmtpSettings(
  orgId: string,
  patch: SmtpSettingsPatch,
): Promise<{ settings: SmtpSettingsPublic }> {
  return apiFetch(`/api/v1/organizations/${orgId}/smtp-settings`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/** Platform fallback when workspace SMTP isn’t set — Dataly admin only. */
export async function fetchPlatformSmtpSettings(): Promise<{ settings: SmtpSettingsPublic }> {
  return apiFetch(`/api/v1/admin/platform/smtp-settings`);
}

export async function patchPlatformSmtpSettings(
  patch: SmtpSettingsPatch,
): Promise<{ settings: SmtpSettingsPublic }> {
  return apiFetch(`/api/v1/admin/platform/smtp-settings`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export type StaffProvisionVaultAccess = {
  vault_id: string;
  permission: "read" | "read_write" | "admin";
};

/** Client admin — create staff user if needed, optional vault ACL, sends welcome email when SMTP is set. */
export async function provisionOrgStaff(
  orgId: string,
  payload: { email: string; vault_access?: StaffProvisionVaultAccess[] },
): Promise<{
  created_user: boolean;
  email_sent: boolean;
  organization_record_id?: string;
  user_record_id?: string;
  membership_record_id?: string;
  temporary_password?: string;
}> {
  return apiFetch(`/api/v1/organizations/${orgId}/members/staff-provision`, {
    method: "POST",
    body: JSON.stringify({
      email: payload.email.trim().toLowerCase(),
      vault_access: payload.vault_access ?? [],
    }),
  });
}

export async function loadApiKeys(orgId: string) {
  const data = await apiFetch<{ api_keys: unknown[] }>(`/api/v1/organizations/${orgId}/api-keys`);
  return data.api_keys;
}

export async function loadSecrets(
  orgId: string,
  opts?: {
    vault_id?: string;
    /** Full-text / ilike search on secret name (server-side). */
    q?: string;
    /** All listed tags must be present (`tags @>` array). */
    tags?: string[];
  },
) {
  const params = new URLSearchParams();
  if (opts?.vault_id) params.set("vault_id", opts.vault_id);
  if (opts?.q) params.set("q", opts.q);
  if (opts?.tags?.length) {
    for (const t of opts.tags) params.append("tag", t);
  }
  const qs = params.toString();
  const data = await apiFetch<{ secrets: unknown[] }>(
    `/api/v1/organizations/${orgId}/secrets${qs ? `?${qs}` : ""}`,
  );
  return data.secrets;
}

export async function createVault(orgId: string, name: string) {
  await apiFetch(`/api/v1/organizations/${orgId}/vaults`, {
    method: "POST",
    body: JSON.stringify({ name, description: null }),
  });
}

export async function createSecret(
  orgId: string,
  vaultId: string,
  name: string,
  value: string,
  metadata: Record<string, unknown>,
) {
  await apiFetch(`/api/v1/organizations/${orgId}/secrets`, {
    method: "POST",
    body: JSON.stringify({
      vault_id: vaultId,
      name,
      value,
      metadata,
    }),
  });
}

export async function createApiKey(
  orgId: string,
  name: string,
  keyPrefix: string,
  keyHash: string,
  scopes: string[],
  vaultIds: string[] | null,
) {
  await apiFetch(`/api/v1/organizations/${orgId}/api-keys`, {
    method: "POST",
    body: JSON.stringify({
      name,
      key_prefix: keyPrefix,
      key_hash: keyHash,
      scopes,
      vault_ids: vaultIds,
    }),
  });
}

export async function revokeApiKey(orgId: string, keyId: string) {
  await apiFetch(`/api/v1/organizations/${orgId}/api-keys/${keyId}`, {
    method: "DELETE",
  });
}

export async function grantVaultAcl(
  orgId: string,
  userId: string,
  vaultId: string,
  permission: "read" | "read_write" | "admin",
) {
  await apiFetch(`/api/v1/organizations/${orgId}/vault-acl`, {
    method: "POST",
    body: JSON.stringify({
      user_id: userId,
      vault_id: vaultId,
      permission,
    }),
  });
}

export type AuditLogRow = {
  id: string;
  record_id?: string;
  created_at: string;
  actor_type: string;
  actor_user_id: string | null;
  actor_api_key_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  metadata: Record<string, unknown>;
  actor_email: string | null;
};

export async function loadAuditLogs(
  orgId: string,
  opts?: { limit?: number; offset?: number; action?: string },
) {
  const q = new URLSearchParams();
  if (opts?.limit != null) q.set("limit", String(opts.limit));
  if (opts?.offset != null) q.set("offset", String(opts.offset));
  if (opts?.action) q.set("action", opts.action);
  const qs = q.toString();
  return apiFetch<{ audit_logs: AuditLogRow[]; total: number; limit: number; offset: number }>(
    `/api/v1/organizations/${orgId}/audit-logs${qs ? `?${qs}` : ""}`,
  );
}

export async function patchSecret(
  orgId: string,
  secretId: string,
  value: string,
  metadata: Record<string, unknown>,
) {
  await apiFetch(`/api/v1/organizations/${orgId}/secrets/${secretId}`, {
    method: "PATCH",
    body: JSON.stringify({ value, metadata }),
  });
}

export function machineSecretsBaseUrl(): string {
  return `${apiBase()}/api/v1/machine/secrets`;
}

/* ─────────────── Admin (dataly_admin only) ─────────────── */

export type AdminOrgRow = {
  id: string;
  record_id?: string;
  name: string;
  slug: string;
  created_at: string;
  /** False when organization is soft-deactivated (members lose tenant access). */
  active: boolean;
  member_count: number;
  vault_count: number;
};

export type AdminMemberRow = {
  user_id: string;
  role: OrgRole;
  joined_at: string;
  email: string;
  display_name: string | null;
  phone: string | null;
};

export type AdminUser = {
  id: string;
  email: string;
  display_name: string | null;
  phone: string | null;
};

export async function adminListOrgs(): Promise<AdminOrgRow[]> {
  const d = await apiFetch<{ orgs: AdminOrgRow[] }>("/api/v1/admin/orgs");
  return d.orgs;
}

export async function adminCreateOrganization(name: string): Promise<{
  id: string;
  record_id?: string;
  name: string;
  slug: string;
  created_at: string;
}> {
  const d = await apiFetch<{
    organization: { id: string; record_id?: string; name: string; slug: string; created_at: string };
  }>(
    "/api/v1/admin/orgs",
    {
      method: "POST",
      body: JSON.stringify({ name: name.trim() }),
    },
  );
  return d.organization;
}

export async function adminDeactivateOrganization(orgId: string): Promise<void> {
  await apiFetch(`/api/v1/admin/orgs/${orgId}/deactivate`, {
    method: "POST",
    body: "{}",
  });
}

export async function adminActivateOrganization(orgId: string): Promise<void> {
  await apiFetch(`/api/v1/admin/orgs/${orgId}/activate`, {
    method: "POST",
    body: "{}",
  });
}

export async function adminDeleteOrganization(orgId: string): Promise<void> {
  await apiFetch(`/api/v1/admin/orgs/${orgId}`, { method: "DELETE" });
}

export async function adminListOrgMembers(orgId: string): Promise<AdminMemberRow[]> {
  const d = await apiFetch<{ members: AdminMemberRow[] }>(
    `/api/v1/admin/orgs/${orgId}/members`,
  );
  return d.members;
}

export async function adminLookupUser(email: string): Promise<AdminUser> {
  const d = await apiFetch<{ user: AdminUser }>(
    `/api/v1/admin/users/lookup?email=${encodeURIComponent(email)}`,
  );
  return d.user;
}

export type AdminClientAdminRow = {
  id: string;
  email: string;
  display_name: string | null;
};

/** Users with `client_admin` in at least one org — org-admin assignment picker. */
export async function adminListClientAdminUsers(): Promise<AdminClientAdminRow[]> {
  const d = await apiFetch<{ users: AdminClientAdminRow[] }>(
    "/api/v1/admin/users/client-admins",
  );
  return d.users;
}

export async function adminSetMemberRole(
  organizationId: string,
  userId: string,
  role: OrgRole,
): Promise<void> {
  await apiFetch(`/api/v1/admin/memberships/role`, {
    method: "POST",
    body: JSON.stringify({ organization_id: organizationId, user_id: userId, role }),
  });
}

export async function adminAddMember(
  organizationId: string,
  email: string,
  role: OrgRole,
): Promise<void> {
  await apiFetch(`/api/v1/admin/memberships`, {
    method: "POST",
    body: JSON.stringify({ organization_id: organizationId, email, role }),
  });
}

export async function adminCreateUser(payload: {
  email: string;
  full_name: string;
  phone?: string;
  organization_id: string;
}): Promise<{
  user: { id: string; email: string; record_id: string };
  organization_id: string;
  email_sent: boolean;
  temporary_password?: string;
}> {
  const body: Record<string, unknown> = {
    email: payload.email.trim(),
    full_name: payload.full_name.trim(),
    phone: payload.phone?.trim() ?? "",
    organization_id: payload.organization_id,
  };
  return apiFetch("/api/v1/admin/users", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function adminRemoveMember(
  organizationId: string,
  userId: string,
): Promise<void> {
  await apiFetch(`/api/v1/admin/orgs/${organizationId}/members/${userId}`, {
    method: "DELETE",
  });
}

export type AdminDirectoryUser = {
  id: string;
  email: string;
  active: boolean;
  created_at: string;
  display_name: string | null;
  phone: string | null;
  org_count: number;
};

export type AdminDirectoryMembership = {
  organization_id: string;
  role: OrgRole;
  org_name: string;
  org_slug: string;
};

export async function adminDirectoryUsers(opts?: {
  limit?: number;
  offset?: number;
  q?: string;
}): Promise<{ users: AdminDirectoryUser[]; total: number; limit: number; offset: number }> {
  const qsp = new URLSearchParams();
  if (opts?.limit != null) qsp.set("limit", String(opts.limit));
  if (opts?.offset != null) qsp.set("offset", String(opts.offset));
  if (opts?.q) qsp.set("q", opts.q);
  const qs = qsp.toString();
  return apiFetch(`/api/v1/admin/users${qs ? `?${qs}` : ""}`);
}

export async function adminDirectoryUserDetail(userId: string): Promise<{
  user: AdminDirectoryUser;
  memberships: AdminDirectoryMembership[];
}> {
  return apiFetch(`/api/v1/admin/users/${userId}`);
}

export async function adminPatchDirectoryUser(
  userId: string,
  body: { email?: string; display_name?: string; phone?: string },
): Promise<{ user: AdminDirectoryUser }> {
  return apiFetch(`/api/v1/admin/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function adminResetDirectoryUserPassword(
  userId: string,
  password: string,
): Promise<void> {
  await apiFetch(`/api/v1/admin/users/${userId}/password`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function adminDeactivateDirectoryUser(userId: string): Promise<void> {
  await apiFetch(`/api/v1/admin/users/${userId}/deactivate`, { method: "POST" });
}

export async function adminActivateDirectoryUser(userId: string): Promise<void> {
  await apiFetch(`/api/v1/admin/users/${userId}/activate`, { method: "POST" });
}

export async function adminDeleteDirectoryUser(userId: string): Promise<void> {
  await apiFetch(`/api/v1/admin/users/${userId}`, { method: "DELETE" });
}

/* ─────────────── Admin: Database explorer ─────────────── */

export type AdminTable = {
  schema: string;
  name: string;
  type: "BASE TABLE" | "VIEW" | string;
};

export type AdminColumn = {
  name: string;
  type: string;
  nullable: "YES" | "NO" | string;
  position: number;
  default_value: string | null;
};

export type AdminRowsResponse = {
  rows: Record<string, unknown>[];
  total: number;
  limit: number;
  offset: number;
};

export type AdminWhereOp =
  | "="
  | "!="
  | "<"
  | "<="
  | ">"
  | ">="
  | "ilike"
  | "is null"
  | "is not null";

export async function adminListTables(): Promise<AdminTable[]> {
  const d = await apiFetch<{ tables: AdminTable[] }>("/api/v1/admin/db/tables");
  return d.tables;
}

export async function adminTableColumns(
  schema: string,
  table: string,
): Promise<AdminColumn[]> {
  const d = await apiFetch<{ columns: AdminColumn[] }>(
    `/api/v1/admin/db/tables/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/columns`,
  );
  return d.columns;
}

export async function adminTableRows(
  schema: string,
  table: string,
  opts: {
    limit?: number;
    offset?: number;
    orderBy?: string;
    orderDir?: "asc" | "desc";
    whereCol?: string;
    whereOp?: AdminWhereOp;
    whereVal?: string;
  } = {},
): Promise<AdminRowsResponse> {
  const q = new URLSearchParams();
  if (opts.limit != null) q.set("limit", String(opts.limit));
  if (opts.offset != null) q.set("offset", String(opts.offset));
  if (opts.orderBy) q.set("order_by", opts.orderBy);
  if (opts.orderDir) q.set("order_dir", opts.orderDir);
  if (opts.whereCol) q.set("where_col", opts.whereCol);
  if (opts.whereOp) q.set("where_op", opts.whereOp);
  if (opts.whereVal != null) q.set("where_val", opts.whereVal);
  const qs = q.toString();
  return apiFetch<AdminRowsResponse>(
    `/api/v1/admin/db/tables/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/rows${qs ? `?${qs}` : ""}`,
  );
}

export type AdminQueryResult = {
  fields: { name: string; dataTypeID: number }[];
  rows: unknown[][];
  row_count: number;
};

export async function adminRunQuery(sql: string): Promise<AdminQueryResult> {
  return apiFetch<AdminQueryResult>(`/api/v1/admin/db/query`, {
    method: "POST",
    body: JSON.stringify({ sql }),
  });
}

/* ─────────────── Secret versioning (REQ-P2-03) ─────────────── */

export type SecretVersionRow = {
  id: string;
  record_id?: string;
  version: number;
  ciphertext: string;
  metadata: Record<string, unknown>;
  comment: string | null;
  created_at: string;
  created_by: string | null;
  actor_email: string | null;
  actor_display_name: string | null;
  value: string | null;
  decrypt_error?: boolean;
};

export async function loadSecretVersions(
  orgId: string,
  secretId: string,
): Promise<SecretVersionRow[]> {
  const d = await apiFetch<{ versions: SecretVersionRow[] }>(
    `/api/v1/organizations/${orgId}/secrets/${secretId}/versions`,
  );
  return d.versions;
}

export async function restoreSecretVersion(
  orgId: string,
  secretId: string,
  version: number,
): Promise<void> {
  await apiFetch(
    `/api/v1/organizations/${orgId}/secrets/${secretId}/restore/${version}`,
    { method: "POST", body: "{}" },
  );
}

export async function deleteSecret(
  orgId: string,
  secretId: string,
  reason?: string,
): Promise<{ approval_required?: boolean; approval_id?: string }> {
  return apiFetch(`/api/v1/organizations/${orgId}/secrets/${secretId}`, {
    method: "DELETE",
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

/* ─────────────── Secret create/patch with tags ─────────────── */

export async function createSecretV2(args: {
  orgId: string;
  vaultId: string;
  name: string;
  value: string;
  metadata?: Record<string, unknown>;
  tags?: string[];
}): Promise<void> {
  await apiFetch(`/api/v1/organizations/${args.orgId}/secrets`, {
    method: "POST",
    body: JSON.stringify({
      vault_id: args.vaultId,
      name: args.name,
      value: args.value,
      metadata: args.metadata ?? {},
      tags: args.tags ?? [],
    }),
  });
}

export async function patchSecretV2(args: {
  orgId: string;
  secretId: string;
  value: string;
  metadata?: Record<string, unknown>;
  tags?: string[];
  reason?: string;
}): Promise<{ approval_required?: boolean; approval_id?: string }> {
  return apiFetch(`/api/v1/organizations/${args.orgId}/secrets/${args.secretId}`, {
    method: "PATCH",
    body: JSON.stringify({
      value: args.value,
      metadata: args.metadata ?? {},
      tags: args.tags ?? [],
      reason: args.reason,
    }),
  });
}

/* ─────────────── Bulk secret ops ─────────────── */

export async function bulkSecretAction(args: {
  orgId: string;
  action: "delete" | "tag" | "untag" | "move";
  secret_ids: string[];
  tags?: string[];
  vault_id?: string;
}): Promise<unknown> {
  return apiFetch(`/api/v1/organizations/${args.orgId}/secrets/bulk`, {
    method: "POST",
    body: JSON.stringify(args),
  });
}

/* ─────────────── Vault settings ─────────────── */

export async function patchVaultSettings(
  orgId: string,
  vaultId: string,
  patch: { name?: string; description?: string; requires_approval?: boolean },
): Promise<unknown> {
  return apiFetch(`/api/v1/organizations/${orgId}/vaults/${vaultId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

/* ─────────────── Webhooks ─────────────── */

export type WebhookRow = {
  id: string;
  record_id?: string;
  name: string;
  url: string;
  events: string[];
  active: boolean;
  created_at: string;
  updated_at: string;
  last_delivery_at: string | null;
  last_status: number | null;
};

export async function listWebhooks(orgId: string): Promise<WebhookRow[]> {
  const d = await apiFetch<{ webhooks: WebhookRow[] }>(
    `/api/v1/organizations/${orgId}/webhooks`,
  );
  return d.webhooks;
}

export async function createWebhook(
  orgId: string,
  body: { name: string; url: string; events?: string[] },
): Promise<{ webhook: WebhookRow; signing_secret: string }> {
  return apiFetch(`/api/v1/organizations/${orgId}/webhooks`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateWebhook(
  orgId: string,
  id: string,
  patch: Partial<{ name: string; url: string; events: string[]; active: boolean }>,
): Promise<{ webhook: WebhookRow }> {
  return apiFetch(`/api/v1/organizations/${orgId}/webhooks/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function deleteWebhook(orgId: string, id: string): Promise<void> {
  await apiFetch(`/api/v1/organizations/${orgId}/webhooks/${id}`, {
    method: "DELETE",
  });
}

export type WebhookDelivery = {
  id: string;
  event: string;
  attempts: number;
  last_status: number | null;
  last_error: string | null;
  next_attempt_at: string;
  delivered_at: string | null;
  failed_at: string | null;
  created_at: string;
};

export async function listWebhookDeliveries(
  orgId: string,
  id: string,
): Promise<WebhookDelivery[]> {
  const d = await apiFetch<{ deliveries: WebhookDelivery[] }>(
    `/api/v1/organizations/${orgId}/webhooks/${id}/deliveries`,
  );
  return d.deliveries;
}

/* ─────────────── Approvals ─────────────── */

export type ApprovalRow = {
  id: string;
  record_id?: string;
  kind: "reveal" | "update" | "delete";
  status: "pending" | "approved" | "rejected" | "cancelled" | "expired";
  reason: string | null;
  requested_by: string | null;
  requester_email: string | null;
  requester_name: string | null;
  resolved_by: string | null;
  resolver_email: string | null;
  resolver_name: string | null;
  resolved_at: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
  secret_id: string | null;
  vault_id: string;
  secret_name: string | null;
  vault_name: string | null;
};

export async function listApprovals(
  orgId: string,
  status: "pending" | "all" = "pending",
): Promise<ApprovalRow[]> {
  const d = await apiFetch<{ approvals: ApprovalRow[] }>(
    `/api/v1/organizations/${orgId}/approvals?status=${status}`,
  );
  return d.approvals;
}

export async function approveApproval(orgId: string, id: string): Promise<void> {
  await apiFetch(`/api/v1/organizations/${orgId}/approvals/${id}/approve`, {
    method: "POST",
    body: "{}",
  });
}

export async function rejectApproval(
  orgId: string,
  id: string,
  reason?: string,
): Promise<void> {
  await apiFetch(`/api/v1/organizations/${orgId}/approvals/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export async function cancelApproval(orgId: string, id: string): Promise<void> {
  await apiFetch(`/api/v1/organizations/${orgId}/approvals/${id}/cancel`, {
    method: "POST",
    body: "{}",
  });
}

/* ─────────────── Audit export ─────────────── */

export function auditExportUrl(
  orgId: string,
  format: "csv" | "ndjson",
  opts?: { action?: string; since?: string; until?: string },
): string {
  const q = new URLSearchParams();
  q.set("format", format);
  if (opts?.action) q.set("action", opts.action);
  if (opts?.since) q.set("since", opts.since);
  if (opts?.until) q.set("until", opts.until);
  return apiUrl(`/api/v1/organizations/${orgId}/audit-logs/export?${q.toString()}`);
}

/** Trigger a download in-browser (audit export is a streaming response). */
export async function downloadAuditExport(
  orgId: string,
  format: "csv" | "ndjson",
  opts?: { action?: string; since?: string; until?: string },
): Promise<void> {
  const token = getToken();
  const url = auditExportUrl(orgId, format, opts);
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = (await res.json()) as { error?: string };
      detail = j.error ?? detail;
    } catch {
      /* response may not be JSON when it's a stream */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const dl = document.createElement("a");
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  dl.href = URL.createObjectURL(blob);
  dl.download = `audit-${stamp}.${format}`;
  document.body.appendChild(dl);
  dl.click();
  dl.remove();
  setTimeout(() => URL.revokeObjectURL(dl.href), 1000);
}

/* ─────────────── REQ-P2-06 Rotation engine ─────────────── */

export type RotationJobRow = {
  id: string;
  organization_id: string;
  secret_id: string | null;
  interval_seconds: number;
  next_run_at: string;
  last_run_at: string | null;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  failure_count: number;
  last_error: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  secret_name: string | null;
  vault_id: string | null;
  vault_name: string | null;
};

export async function listRotationJobs(orgId: string): Promise<RotationJobRow[]> {
  const j = await apiFetch<{ rotation_jobs: RotationJobRow[] }>(
    `/api/v1/organizations/${orgId}/rotation-jobs`,
  );
  return j.rotation_jobs;
}

export async function createRotationJob(
  orgId: string,
  args: {
    secret_id: string;
    interval_seconds: number;
    metadata?: Record<string, unknown>;
  },
): Promise<{ rotation_job_id: string }> {
  return apiFetch<{ rotation_job_id: string }>(
    `/api/v1/organizations/${orgId}/rotation-jobs`,
    { method: "POST", body: JSON.stringify(args) },
  );
}

export async function deleteRotationJob(orgId: string, id: string): Promise<void> {
  await apiFetch<{ ok: true }>(
    `/api/v1/organizations/${orgId}/rotation-jobs/${id}`,
    { method: "DELETE" },
  );
}

export async function runRotationJobNow(orgId: string, id: string): Promise<void> {
  await apiFetch<{ ok: true }>(
    `/api/v1/organizations/${orgId}/rotation-jobs/${id}/run-now`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

/* ─────────────── REQ-P2-07 OIDC SSO ─────────────── */

export type OidcProvider = { id: string; label: string };

export async function listOidcProviders(): Promise<OidcProvider[]> {
  const j = await apiFetch<{ providers: OidcProvider[] }>(`/api/v1/oidc/providers`);
  return j.providers;
}

export function oidcLoginUrl(providerId: string, returnTo?: string): string {
  const q = new URLSearchParams();
  if (returnTo) q.set("return_to", returnTo);
  const qs = q.toString();
  return apiUrl(`/api/v1/oidc/login/${encodeURIComponent(providerId)}${qs ? `?${qs}` : ""}`);
}

/* ─────────────── REQ-P2-10 Audit chain verify + retention ─────────────── */

export type AuditVerifyResponse = {
  ok: boolean;
  total_checked: number;
  last_hash: string;
  first_bad: { id: string; chain_seq: string } | null;
  note: string;
};

export async function adminVerifyAuditChain(limit = 5000): Promise<AuditVerifyResponse> {
  return apiFetch<AuditVerifyResponse>(
    `/api/v1/admin/audit/verify?limit=${encodeURIComponent(String(limit))}`,
  );
}

export async function adminRunAuditRetention(days = 730): Promise<{ ok: boolean; removed: number; retention_days: number }> {
  return apiFetch<{ ok: boolean; removed: number; retention_days: number }>(
    `/api/v1/admin/audit/retention/run`,
    { method: "POST", body: JSON.stringify({ days }) },
  );
}

/* ─────────────── REQ-P2-12 Per-org envelope encryption ─────────────── */

export async function adminEnableOrgEnvelope(orgId: string): Promise<{ ok: boolean; active_data_key_id: string }> {
  return apiFetch<{ ok: boolean; active_data_key_id: string }>(
    `/api/v1/admin/orgs/${orgId}/envelope-encryption/enable`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function adminRotateOrgEnvelope(orgId: string): Promise<{ ok: boolean; active_data_key_id: string }> {
  return apiFetch<{ ok: boolean; active_data_key_id: string }>(
    `/api/v1/admin/orgs/${orgId}/envelope-encryption/rotate`,
    { method: "POST", body: JSON.stringify({}) },
  );
}
